import React, { useMemo, useState } from 'react';
import I18n from 'i18n-js';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { RCButton, RCIcon } from '@reverbdotcom/cadence/components';
import { NudgeExploreIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';

import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { INSIGHT, trackClicked, useTrackingContext, useTrackImpression } from './sellerRecommendationTracking';

interface Props {
  listing: Listing;
}

export const COMPONENT_NAME = 'TopPickEligiblePresentment';

export default function TopPickEligiblePresentment({ listing }: Props) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const recommendedRate = listing.featuredListingInsight?.recommendedBumpRate;
  const bumpRate = listing.bumpRate?.rate || 0;
  const trackingContext = useTrackingContext();

  const details = { recommendedBumpRate: recommendedRate ?? undefined };

  // Memoized on the rate rather than the details object: this reaches the bump modal's
  // success effect deps, and a fresh object each render would re-fire it.
  const bumpSource = useMemo(
    () => ({ insight: INSIGHT.TOP_PICK_ELIGIBLE, componentName: COMPONENT_NAME, details: { recommendedBumpRate: recommendedRate ?? undefined } }),
    [recommendedRate],
  );

  useTrackImpression(COMPONENT_NAME, INSIGHT.TOP_PICK_ELIGIBLE, listing.id, details);

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className="seller-listing-insight-presentment__top-pick-eligible">
          <RCIcon icon={<NudgeExploreIcon />} size="inline-prominent" spacing="right" color="accent-loud" />
          <span className="weight-bold">
            {I18n.t('discovery.listingsManagement.sellerListingCard.insights.topPickEligible.title')}
          </span>
        </span>
        <RCTextUtility size="350">
          <Translate text="discovery.listingsManagement.sellerListingCard.insights.topPickEligible.bumpToWin">
            <span className="weight-semibold">{recommendedRate}%</span>
          </Translate>
        </RCTextUtility>
        <RCButton
          variant="outlined"
          size="small"
          onClick={() => {
            trackClicked(COMPONENT_NAME, INSIGHT.TOP_PICK_ELIGIBLE, listing.id, trackingContext, details);
            setShowBumpModal(true);
          }}
          fullWidth
        >
          {I18n.t('discovery.listingsManagement.sellerListingCard.promoteWithBump')}
        </RCButton>
      </div>

      <BumpModalV2WithController
        listingId={listing.id}
        bid={bumpRatePercentToDecimal(bumpRate)}
        showModal={showBumpModal}
        setShowModal={setShowBumpModal}
        listing={listing}
        source={bumpSource}
      />
    </>
  );
}
